import { useEffect, useState } from "react";
import { useInstallPrompt, isIOS } from "@/hooks/useInstallPrompt";

const STORAGE_KEY = "yuo_pwa_welcome_dismissed";

export default function PWAWelcomeModal() {
  const { available, promptInstall, isStandalone } = useInstallPrompt();
  const [show, setShow] = useState(false);
  const [dontShow, setDontShow] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Never show inside an installed standalone window.
    if (isStandalone) return;
    // Respect dismissal flag.
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {}
    // Show shortly after mount so login/UI settles first.
    const t = setTimeout(() => setShow(true), 1600);
    return () => clearTimeout(t);
  }, [isStandalone]);

  function close() {
    if (dontShow) { try { localStorage.setItem(STORAGE_KEY, "1"); } catch {} }
    setShow(false);
  }
  async function onInstall() {
    setInstalling(true);
    const r = await promptInstall();
    setInstalling(false);
    if (r?.outcome === "accepted") { try { localStorage.setItem(STORAGE_KEY, "1"); } catch {}; setShow(false); }
  }

  if (!show) return null;
  const ios = isIOS();

  return (
    <div data-testid="pwa-welcome-modal" style={overlay} onClick={close}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={header}>
          <span style={stone} />
          <div>
            <div style={{ fontSize: 11, letterSpacing: "0.24em", color: "var(--fg-42)", textTransform: "uppercase" }}>
              Welcome to YUO
            </div>
            <div style={{ fontSize: 20, marginTop: 4, letterSpacing: "-0.01em", color: "var(--yuo-ink)", fontFamily: '"JetBrains Mono", monospace' }}>
              Install YUO as an app
            </div>
          </div>
          <button data-testid="pwa-close" onClick={close} style={xBtn}>✕</button>
        </div>

        <div style={{ ...text, marginBottom: 14 }}>
          The YUO icon will live on your Home screen / in Applications, and the app opens in a clean window without the browser bar. All recordings, diary entries and drafts stay locally on your device.
        </div>

        {ios ? (
          <div style={box}>
            <div style={boxTitle}>iPad / iPhone · Safari</div>
            <div style={step}>1 · Tap the <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>“Share”</b> ⤴︎ icon in the bottom browser bar</div>
            <div style={step}>2 · Choose <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>“Add to Home Screen”</b></div>
            <div style={step}>3 · Tap <b>“Add”</b> — YUO appears as an app with its own icon</div>
          </div>
        ) : (
          <>
            <div style={box}>
              <div style={boxTitle}>Mac / Windows · Chrome, Arc, Edge</div>
              <div style={step}>Press the button below — the browser will ask for confirmation and install YUO as an app.</div>
              <button
                data-testid="pwa-install-now"
                disabled={!available || installing}
                onClick={onInstall}
                style={{ ...bigBtn, opacity: (!available || installing) ? 0.4 : 1 }}
              >
                {installing ? "…" : available ? "⤓ Install YUO as an app" : "The button appears once the browser allows it"}
              </button>
              {!available && (
                <div style={{ ...step, marginTop: 8, color: "var(--fg-4)" }}>
                  If the button is inactive — open the browser menu at the top right → <b>“Install YUO Studio”</b> / <b>“Install app”</b>.
                </div>
              )}
            </div>
            <div style={box}>
              <div style={boxTitle}>iPad / iPhone · Safari</div>
              <div style={step}>1 · <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>Share</b> ⤴︎  →  <b style={{ color: "rgba(var(--role-green-rgb),.9)" }}>“Add to Home Screen”</b>  →  <b>Add</b></div>
            </div>
          </>
        )}

        <div style={{ ...text, marginTop: 6, fontSize: 11.5, color: "var(--fg-5)" }}>
          Everything you create in YUO is processed in the browser. No video, audio or notes are sent to remote servers. The author is the sole owner of the created materials.
        </div>

        <div style={footer}>
          <label data-testid="pwa-dont-show" style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 11, letterSpacing: "0.1em", color: "var(--fg-5)" }}>
            <input type="checkbox" checked={dontShow} onChange={(e) => setDontShow(e.target.checked)} />
            don't show again
          </label>
          <button data-testid="pwa-later" onClick={close} style={laterBtn}>later</button>
        </div>
      </div>
      <style>{`@keyframes yuoPwaReveal{from{opacity:0;transform:translateY(20px) scale(.98);filter:blur(12px)}to{opacity:1;transform:none;filter:blur(0)}}`}</style>
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 1000,
  background: "radial-gradient(120% 120% at 30% 0%, rgba(5,6,12,.72), rgba(5,6,12,.94))",
  backdropFilter: "blur(20px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20, animation: "yuoPwaReveal .5s both",
};
const panel = {
  width: "100%", maxWidth: 480,
  padding: "26px 26px 20px", borderRadius: 32,
  background: "radial-gradient(120% 100% at 26% 0%, rgba(var(--role-green-rgb),.08), var(--fg-04) 40%, rgba(5,6,12,.9) 100%)",
  boxShadow: "0 0 60px rgba(var(--role-green-rgb),.18), inset 0 0 60px rgba(var(--role-green-rgb),.04)",
  color: "var(--yuo-ink)",
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  animation: "yuoPwaReveal .55s both",
};
const header = { display: "flex", alignItems: "center", gap: 14, marginBottom: 18 };
const stone = {
  width: 42, height: 42, borderRadius: "50%",
  background: "radial-gradient(34% 30% at 32% 26%, #fff, #34FF9B 22%, #0D3A26 62%, #01040a 100%)",
  boxShadow: "0 0 22px rgba(var(--role-green-rgb),.6)", flex: "0 0 auto",
};
const xBtn = {
  marginLeft: "auto", width: 34, height: 34, borderRadius: "50%",
  background: "var(--fg-05)", color: "var(--fg-5)",
  border: "none", cursor: "pointer", fontSize: 14,
};
const text = { fontSize: 13, lineHeight: 1.65, color: "var(--fg-75)" };
const box = {
  padding: "16px 18px", borderRadius: 22, marginBottom: 12,
  background: "var(--fg-04)",
};
const boxTitle = {
  fontSize: 10, letterSpacing: "0.24em", color: "rgba(198,255,224,.7)",
  textTransform: "uppercase", marginBottom: 10,
};
const step = { fontSize: 12.5, lineHeight: 1.7, color: "var(--fg-72)", paddingLeft: 4 };
const bigBtn = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 10,
  marginTop: 12, padding: "12px 18px", borderRadius: 999, cursor: "pointer",
  fontFamily: 'inherit', fontSize: 11.5, letterSpacing: "0.18em", textTransform: "uppercase",
  color: "rgba(var(--role-green-rgb),.98)", border: "none",
  background: "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-green-rgb),.34), rgba(var(--role-green-rgb),.08))",
  boxShadow: "0 0 30px rgba(var(--role-green-rgb),.32)", width: "100%",
};
const footer = {
  marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
};
const laterBtn = {
  padding: "10px 20px", borderRadius: 999, border: "none", cursor: "pointer",
  fontFamily: "inherit", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
  background: "var(--fg-06)", color: "var(--fg-7)",
};
